import {CSSProperties} from 'react'

/**
 * https://developer.mozilla.org/en-US/docs/Web/CSS/font-weight
 */
export type FontWeight =
  | '100'
  | '200'
  | '300'
  | '400'
  | '500'
  | '600'
  | '700'
  | '800'
  | '900'
  | 'normal' // AKA 400
  | 'bold' // AKA 700
  // Relative to parent
  | 'lighter'
  | 'bolder'

/**
 * https://developer.mozilla.org/en-US/docs/Web/CSS/font-style
 */
export type FontStyle = 'normal' | 'italic' | 'oblique'

/**
 * https://developer.mozilla.org/en-US/docs/Web/CSS/align-items
 */
export type AlignItems =
  // Basic
  | 'normal'
  | 'stretch'
  // Positional alignment
  | 'center'
  | 'start'
  | 'end'
  | 'flex-start'
  | 'flex-end'
  // Baseline alignment
  | 'baseline'
  | 'first baseline'
  | 'last baseline'
  | 'safe center'
  | 'unsafe center'

type Align =
  // Positional alignment
  | 'center'
  | 'start'
  | 'end'
  | 'flex-start'
  | 'flex-end'
  // Normal alignment
  | 'normal'
  // Baseline alignment
  | 'baseline'
  | 'first baseline'
  | 'last baseline'
  | 'stretch'

/**
 * https://developer.mozilla.org/en-US/docs/Web/CSS/align-content
 */
export type AlignContent =
  | Align
  // Distributed alignment
  | 'space-between'
  | 'space-around'
  | 'space-evenly'
  // Overflow alignment
  | 'safe center'
  | 'unsafe center'

/**
 * https://developer.mozilla.org/en-US/docs/Web/CSS/align-self
 */
export type AlignSelf = Align | 'auto' | 'self-start' | 'self-end'

/**
 * https://developer.mozilla.org/en-US/docs/Web/CSS/text-align
 */
export type TextAlign = 'left' | 'right' | 'center' | 'justify' | 'justify-all' | 'start' | 'end' | 'match-parent'

/**
 * https://developer.mozilla.org/en-US/docs/Web/CSS/flex-direction
 */
export type FlexDirection = 'row' | 'column' | 'row-reverse' | 'column-reverse'

/**
 * https://developer.mozilla.org/en-US/docs/Web/CSS/justify-content
 */
export type JustifyContent =
  // Normal alignment
  | 'normal'
  // Positional alignment
  | 'center'
  | 'start'
  | 'end'
  | 'flex-start'
  | 'flex-end'
  | 'left'
  | 'right'
  // Distributed alignment
  | 'space-around'
  | 'space-between'
  | 'space-evently'
  | 'stretch'
  // Overflow alignment
  | 'safe center'
  | 'unsafe center'

/**
 * https://developer.mozilla.org/en-US/docs/Web/CSS/display
 */
export type Display =
  // Legacy values
  | 'flex'
  | 'block'
  | 'inline'
  | 'inline-block'
  | 'inline-flex'
  | 'grid'
  | 'inline-grid'
  | 'flow-root'
  // Box generation
  | 'none'
  | 'contents'
  // Two value syntax
  | 'block flow'
  | 'inline flow'
  | 'inline flow-root'
  | 'block flex'
  | 'inline flex'
  | 'block grid'
  | 'inline grid'
  | 'block flow-root'
  // Other values
  | 'table'
  | 'table-row'
  | 'list-item'

/**
 * https://developer.mozilla.org/en-US/docs/Web/CSS/visibility
 */
export type Visibility = 'visible' | 'hidden' | 'collapse'

/**
 * https://developer.mozilla.org/en-US/docs/Web/CSS/border-style
 */
export type BorderStyle =
  | 'none'
  | 'hidden'
  | 'solid'
  | 'dotted'
  | 'dashed'
  | 'double'
  | 'groove'
  | 'ridge'
  | 'inset'
  | 'outset'

/**
 * https://developer.mozilla.org/en-US/docs/Web/CSS/overflow
 */
export type Overflow = 'hidden' | 'visible' | 'scroll' | 'auto'

/**
 * https://developer.mozilla.org/en-US/docs/Web/CSS/position
 */
export type Position = 'static' | 'absolute' | 'relative' | 'fixed' | 'sticky'

/**
 * https://developer.mozilla.org/en-US/docs/Web/CSS/white-space
 */
export type Whitespace = 'normal' | 'nowrap' | 'pre' | 'pre-wrap' | 'pre-line' | 'break-spaces'

/**
 * https://developer.mozilla.org/en-US/docs/Web/CSS/flex-wrap
 */
export type FlexWrap = 'nowrap' | 'wrap' | 'wrap-reverse'

/**
  https://developer.mozilla.org/en-US/docs/Web/CSS/text-overflow
  TextOverflow only affects layout if:
    overflow: hidden;
    white-space: nowrap;
*/
export type TextOverflow = 'ellipsis' | 'clip'

/**
 * https://developer.mozilla.org/en-US/docs/Web/CSS/text-decoration-line
 */
export type TextDecorationLine =
  | 'none'
  | 'underline'
  | 'overline'
  | 'line-through'
  | 'underline overline'
  | 'underline line-through'

/**
 * https://developer.mozilla.org/en-US/docs/Web/CSS/text-decoration-style
 */
export type TextDecorationStyle = 'solid' | 'double' | 'dotted' | 'dashed' | 'wavy'

/**
 * https://developer.mozilla.org/en-US/docs/Web/CSS/overflow-wrap
 */
export type OverflowWrap = 'normal' | 'break-word' | 'anywhere'

/**
 * https://developer.mozilla.org/en-US/docs/Web/CSS/word-break
 */
export type WordBreak = 'normal' | 'break-all' | 'break-word' | 'keep-all'

/**
 * https://developer.mozilla.org/en-US/docs/Web/CSS/pointer-events
 */
export type PointerEvents =
  | 'auto'
  | 'none'
  | 'visibleFill' /* SVG only */
  | 'visibleStroke' /* SVG only */
  | 'visible' /* SVG only */
  | 'painted' /* SVG only */
  | 'fill' /* SVG only */
  | 'stroke' /* SVG only */
  | 'all' /* SVG only */

/**
 * https://developer.mozilla.org/en-US/docs/Web/CSS/object-fit
 */
export type ObjectFit = 'contain' | 'cover' | 'fill' | 'none' | 'scale-down'

/**
 * https://developer.mozilla.org/en-US/docs/Web/CSS/object-position
 */
export type ObjectPosition = 'bottom' | 'center' | 'left' | 'right' | 'top' // TODO: add support for `50% 50%` and `10px 34px`

/**
 * https://developer.mozilla.org/en-US/docs/Web/CSS/box-sizing
 */
export type BoxSizing = 'border-box' | 'content-box'

export type StyleValues = CSSProperties | ((...arg: any[]) => CSSProperties)
